/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Host, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  CELL_TEXT_PRIMARY_CLASS,
  code3Formatter,
  falsyCellRenderer, GREEN_MARKER_COLOR, rendererColorCircle,
  rendererIconByName, truthyCellRenderer,
} from '@gh/common-internal-shared';
import { getDialogInitData, getDialogInput, MutableSelectionModel, NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_ROW_LAYOUT } from '@gh/core-ui';
import { buildNumberColumn, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { RowSelectedEvent } from 'ag-grid-community';
import { getPbCcFinderListItemId, getPbComponentItemFinderListItemId } from '../../';
import { PbComponentItemFinderDialogConfig } from '../../resources/pb-component-item-finder-business.service';
import {
  PbComponentItemFinderCcListItem,
  PbComponentItemFinderListItem,
} from '../../resources/pb-component-item-finder.model';
import { AbstractPbComponentItemFinderViewComponent } from './abstract-pb-component-item-finder-view-component';
import { PbComponentItemFinderDialogComponent } from './pb-component-item-finder-dialog.component';

@Component({
  selector: 'gh-pb-component-item-finder-cc-view',
  template: require('./pb-component-item-finder-cc-view.component.html'),
  styles: [require('./pb-component-item-finder-cc-view.component.scss')],
})
export class PbComponentItemFinderCcViewComponent extends AbstractPbComponentItemFinderViewComponent implements OnInit {
  input: PbComponentItemFinderDialogConfig;
  selectionModel: MutableSelectionModel<PbComponentItemFinderListItem>;
  ccGridDefinition: GridDefinition;
  structureGridDefinition: GridDefinition;

  costCenters: PbComponentItemFinderCcListItem[];
  currentStructures: PbComponentItemFinderListItem[];
  selectedCostCenterId?: ID;

  constructor(private gridDefinitionFactory: GridDefinitionFactory,
              private translationService: TranslationService,
              @Host() private finderDialog: PbComponentItemFinderDialogComponent,
              @Inject(MAT_DIALOG_DATA) private dialogData: any) {
    super();
  }

  ngOnInit(): void {
    const { hierarchy } = getDialogInitData(this.dialogData);

    this.input = getDialogInput(this.dialogData);
    this.selectionModel = this.finderDialog.selectionModel;

    this.costCenters = hierarchy;

    this.ccGridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'code',
          width: 80,
          headerName: this.translationService.get('lbl_number'),
          valueFormatter: code3Formatter,
          cellClass: CELL_TEXT_PRIMARY_CLASS,
        },
        { field: 'name', width: 120, headerName: this.translationService.get('lbl_description') },
      ],
      {
        rowSelection: 'single',
        getRowNodeId: getPbCcFinderListItemId,
      });

    this.structureGridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'code',
          width: 150,
          headerName: this.translationService.get('lbl_code'),
          cellClass: CELL_TEXT_PRIMARY_CLASS,
          suppressSizeToFit: true,
        },
        { field: 'name', width: 60, headerName: this.translationService.get('lbl_name') },
        {
          field: 'isActive',
          width: 90,
          headerName: this.translationService.get('lbl_deleted'),
          cellRenderer: falsyCellRenderer(rendererIconByName('done')),
          suppressSizeToFit: true,
        },
        {
          field: 'uomCode',
          width: 70,
          headerName: this.translationService.get('lbl_uom_abbreviation'),
          suppressSizeToFit: true,
        },
        {
          field: 'price',
          width: 90,
          headerName: this.translationService.get('lbl_price'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Price),
        },
        {
          field: 'origQuantity',
          width: 110,
          headerName: this.translationService.get('lbl_orig_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
        {
          field: 'origQuantityComponent',
          width: 110,
          headerName: this.translationService.get('lbl_cmp_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
      ],
      {
        getRowNodeId: getPbComponentItemFinderListItemId,
      });

    this.selectCostCenter(this.costCenters[0]);
  }

  onRowSelected(event: RowSelectedEvent): void {
    this.selectCostCenter(event.data);
  }

  private selectCostCenter(data?: PbComponentItemFinderCcListItem): void {
    if (data) {
      const { costCenterId } = data;
      this.selectedCostCenterId = costCenterId;
      this.currentStructures = data.items;
    } else {
      this.selectedCostCenterId = void 0;
    }
  }
}
